import React from 'react'
import dayjs from 'dayjs'
import { Table } from 'antd'
import {
  getUserIdentifierFromRecord,
  getEventData,
  formatMs,
  formatTs,
  toMs,
  filterUsageEventsByDate,
} from '../utils'
import { useColumnSearch } from './useColumnSearch'

// Event types this table cares about
const REL_EVT_TYPES = ['GLOBAL_SEARCH_PERFORMED']

// Human-readable labels for event types
export const EVENT_TYPE_LABELS = {
  GLOBAL_SEARCH_PAGE_LOADED: 'page open',
  GLOBAL_SEARCH_PAGE_TIME_SPENT: 'page close',
  GLOBAL_SEARCH_PERFORMED: 'search',
}

export default function GlobalSearchTable({
  data,
  plantFilter,
  selectedDate,
  selectedUser,
  userIdentifier = 'user_id',
}) {
  const [filtered, setFiltered] = React.useState([])

  const getRecordTimeSpent = (record) => getEventData(record).timeOnPage || 0

  // Deduplication: merge LOADED + TIME_SPENT pairs by session_id.
  // GLOBAL_SEARCH_PERFORMED events are left as individual rows.
  const dedupeEvents = (events) => {
    // Separate session boundary events from search events
    const loadedEvents = events.filter(
      (ev) => ev.eventType === 'GLOBAL_SEARCH_PAGE_LOADED',
    )
    const timeSpentEvents = events.filter(
      (ev) => ev.eventType === 'GLOBAL_SEARCH_PAGE_TIME_SPENT',
    )
    const searchEvents = events.filter(
      (ev) => ev.eventType === 'GLOBAL_SEARCH_PERFORMED',
    )

    // Index loaded events by session_id (or fallback key)
    const startsBySession = {}
    loadedEvents.forEach((ev) => {
      const sessionId = getEventData(ev).session_id
      const identifier = getUserIdentifierFromRecord(ev, userIdentifier)
      const plant = getEventData(ev).plant || 'unknown'
      const key = sessionId
        ? `session-${sessionId}`
        : `nosession-${identifier}-${plant}-${dayjs(ev.loggedAt).toISOString()}`
      startsBySession[key] = ev
    })

    // Index time-spent events by session_id (or fallback key)
    const endsBySession = {}
    timeSpentEvents.forEach((ev) => {
      const sessionId = getEventData(ev).session_id
      const identifier = getUserIdentifierFromRecord(ev, userIdentifier)
      const plant = getEventData(ev).plant || 'unknown'
      const key = sessionId
        ? `session-${sessionId}`
        : `nosession-${identifier}-${plant}-${dayjs(ev.loggedAt - getRecordTimeSpent(ev)).toISOString()}`
      // If multiple TIME_SPENT events map to the same session, keep the latest
      if (!endsBySession[key] || ev.loggedAt > endsBySession[key].loggedAt) {
        endsBySession[key] = ev
      }
    })

    // For each session: prefer TIME_SPENT (has duration info), fall back to LOADED
    const sessionRows = []
    const allKeys = new Set([
      ...Object.keys(startsBySession),
      ...Object.keys(endsBySession),
    ])
    allKeys.forEach((key) => {
      if (endsBySession[key]) {
        sessionRows.push(endsBySession[key])
      } else if (startsBySession[key]) {
        sessionRows.push(startsBySession[key])
      }
    })

    return [...sessionRows, ...searchEvents]
  }

  const filterEvents = (
    data,
    { selectedUser, plantFilter, selectedDate, userIdentifier },
  ) => {
    let filtered = Array.isArray(data)
      ? data.filter(
          (ev) => ev.eventType && REL_EVT_TYPES.includes(ev.eventType),
        )
      : []

    if (selectedUser) {
      filtered = filtered.filter((row) => {
        const identifier = getUserIdentifierFromRecord(row, userIdentifier)
        return identifier === selectedUser
      })
    }

    if (plantFilter) {
      filtered = filtered.filter((row) => {
        try {
          const plant = getEventData(row).plant || ''
          return plant === plantFilter
        } catch {
          return false
        }
      })
    }

    filtered = filterUsageEventsByDate(filtered, selectedDate)

    return dedupeEvents(filtered)
  }

  React.useEffect(() => {
    const result = filterEvents(data, {
      selectedUser,
      plantFilter,
      selectedDate,
      userIdentifier,
    })
    setFiltered(result)
  }, [data, selectedUser, plantFilter, selectedDate, userIdentifier])

  const { getColumnSearchProps } = useColumnSearch([
    'user',
    'plant',
    'searchTerm',
  ])

  const columns = [
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      ...getColumnSearchProps(
        'user',
        (record) => getUserIdentifierFromRecord(record, userIdentifier),
        'Search user',
      ),
      sorter: (a, b) => {
        const uA = getUserIdentifierFromRecord(a, userIdentifier) || ''
        const uB = getUserIdentifierFromRecord(b, userIdentifier) || ''
        return uA.localeCompare(uB)
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Search Term',
      dataIndex: 'searchTerm',
      key: 'searchTerm',
      ...getColumnSearchProps(
        'searchTerm',
        (record) => {
          if (record.eventType !== 'GLOBAL_SEARCH_PERFORMED') return ''
          return getEventData(record).searchString || ''
        },
        'Search term',
      ),
      render: (val, record) => {
        if (record.eventType !== 'GLOBAL_SEARCH_PERFORMED') return '-'
        return getEventData(record).searchString || '-'
      },
      sorter: (a, b) => {
        const sA =
          a.eventType === 'GLOBAL_SEARCH_PERFORMED'
            ? getEventData(a).searchString || ''
            : ''
        const sB =
          b.eventType === 'GLOBAL_SEARCH_PERFORMED'
            ? getEventData(b).searchString || ''
            : ''
        return sA.localeCompare(sB)
      },
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Timestamp',
      dataIndex: 'start',
      key: 'start',
      render: (val, record) => {
        if (
          record.eventType === 'GLOBAL_SEARCH_PAGE_LOADED' ||
          record.eventType === 'GLOBAL_SEARCH_PERFORMED'
        ) {
          return formatTs(record.loggedAt)
        }
      },
      sorter: (a, b) => {
        const aStart =
          a.eventType === 'GLOBAL_SEARCH_PAGE_TIME_SPENT'
            ? toMs(a.loggedAt) - getRecordTimeSpent(a)
            : toMs(a.loggedAt)
        const bStart =
          b.eventType === 'GLOBAL_SEARCH_PAGE_TIME_SPENT'
            ? toMs(b.loggedAt) - getRecordTimeSpent(b)
            : toMs(b.loggedAt)
        return aStart - bStart
      },
      sortDirections: ['ascend', 'descend'],
    },
  ]

  return (
    <Table
      columns={columns}
      dataSource={filtered}
      rowKey={(row, idx) => row.id || idx}
      pagination={{ pageSize: 5, showSizeChanger: false }}
      size="small"
    />
  )
}
